import { useSearchExtendedAccommodationProperties, useAccommodationPropertiesWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { ScoringPriorityField } from '@graphql/generated';
import { VenueDialogAction } from './useEventVenueDialogReducer';
import { addAction } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEffect, useRef } from 'react';
import { searchRadiiKm } from '../components/EventVenueDialog/EventVenueDialog';

const EVENT_VENUE_DIALOG_CONTEXT = 'EventVenueDialog';

interface UseHotelSearchProps {
  shouldSearch: boolean;
  destination: google.maps.LatLngLiteral;
  scoringPriorityFields: ScoringPriorityField[];
  searchRadiiKm: typeof searchRadiiKm;
  dispatch: React.Dispatch<VenueDialogAction>;
}

export const useHotelSearch = ({ shouldSearch, destination, scoringPriorityFields, searchRadiiKm, dispatch }: UseHotelSearchProps) => {
  const currentRadiusIndexRef = useRef(0);

  const { eventHandle } = useEventInfo();

  const { data, error, loading, fetchSearchExtendedAccommodationProperties } = useSearchExtendedAccommodationProperties(
    { latitude: destination.lat, longitude: destination.lng },
    scoringPriorityFields,
    !shouldSearch, // Skip fetch when shouldSearch is false
    EVENT_VENUE_DIALOG_CONTEXT,
  );

  const properties = data?.searchExtendedAccommodationProperties.properties ?? [];

  const accommodationPropertyWithCalculatedPrice = useAccommodationPropertiesWithCalculatedPrice(properties, destination);

  const hasData = accommodationPropertyWithCalculatedPrice.length > 0;

  // Notify when search has started
  useEffect(() => {
    if (loading) {
      dispatch({ type: 'SEARCH_FOR_PROPERTIES_STARTED' });
    }
  }, [loading, dispatch]);

  // Handle retry if there was an error and no retry has occurred yet
  useEffect(() => {
    if (!shouldSearch || loading) return;

    if ((error || !hasData) && currentRadiusIndexRef.current < searchRadiiKm.length) {
      const reasonForRetry = error ? 'failure' : 'radius_increase';
      addAction('retrySearchApiCall', { message: 'Retry search API call', context: EVENT_VENUE_DIALOG_CONTEXT, eventHandle, destination, reasonForRetry });

      // Retry once on failed
      if (error) {
        fetchSearchExtendedAccommodationProperties(scoringPriorityFields, searchRadiiKm[0]);
        currentRadiusIndexRef.current = searchRadiiKm.length;

        // Retry on empty results
      } else if (!hasData) {
        const searchRadiusKm = searchRadiiKm[currentRadiusIndexRef.current];

        currentRadiusIndexRef.current += 1;

        // Skip first/default index where its already searched with first time
        if (currentRadiusIndexRef.current !== 0) {
          fetchSearchExtendedAccommodationProperties(scoringPriorityFields, searchRadiusKm);
        }
      }
    } else if (error) {
      dispatch({ type: 'SEARCH_FOR_PROPERTIES_ERROR' });
    }
  }, [shouldSearch, loading, error, eventHandle, destination, fetchSearchExtendedAccommodationProperties, scoringPriorityFields, dispatch, hasData, searchRadiiKm]);

  // Handle success or final error after retry
  useEffect(() => {
    if (!shouldSearch || loading || error || (currentRadiusIndexRef.current < searchRadiiKm.length && !hasData)) return;

    if (hasData) {
      dispatch({ type: 'SEARCH_FOR_PROPERTIES_SUCCESS', accommodationPropertyWithCalculatedPrice });
    } else {
      dispatch({ type: 'SEARCH_FOR_PROPERTIES_ERROR' });
    }
  }, [shouldSearch, loading, error, hasData, accommodationPropertyWithCalculatedPrice, dispatch, searchRadiiKm.length]);
};
